import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs/internal/Observable';
import * as urltemplate from 'url-template';
import { catchError } from 'rxjs/operators';
import { throwError } from 'rxjs';
import { LMLoggerService } from './lm-logger.service';
import { LMUrlUtilService } from './utils';
import {
  LMFieldPickerIncludeOptions,
  LMFieldPickerRequest,
  LMFieldPickerResponse,
  LMFieldPickerSearchComparator,
  LMFieldPickerView
} from '@modal/field-picker/lm-field-picker';
import { ListRequestType } from '@app/utils';

@Injectable()
export class LMConfigurationService {

  constructor(private http: HttpClient,
    private lmLoggerService: LMLoggerService,
    private lmUrlUtilService: LMUrlUtilService) {
  }

  public getPersonalization(personalizeUrl: any, userFolder: string, showPersonalizeOption = false): Observable<LMPersonalizeResponse> {
    personalizeUrl = this.lmUrlUtilService.updateUrlParams(personalizeUrl, { userfolder: userFolder, showPersonalizeOption: showPersonalizeOption});

    const headers = new HttpHeaders();
    headers.append('Content-Type', 'application/json;charset=utf-8');
    return <Observable<LMPersonalizeResponse>> this.http.get(personalizeUrl, { headers });
  }
  // this method should be used in the UI for auto-completion
  public searchField(searchTerm: string, url: string, busClassName: string, mode: LMFieldPickerView, includeOptions?: LMFieldPickerIncludeOptions[]): Observable<LMFieldPickerResponse> {
    let comparator: LMFieldPickerSearchComparator = 'contains';
    if (searchTerm?.indexOf('.') !== -1) {
      const structure = searchTerm.split('.');
      const lastStructure = structure[structure.length -1];
      comparator = lastStructure.length < 3 ? 'startsWith' : 'contains';
    } else {
      comparator = searchTerm?.length < 3 ? 'startsWith' : 'contains';
    }

    const formRequest: LMFieldPickerRequest = {
      requestType: ListRequestType.FieldSpecForBusClass,
      businessClass: busClassName,
      view: mode,
      searchOptions: {
        nameValue: searchTerm,
        nameComparator: comparator,
        autoComplete: true
      },
      includeOptions,
    };

    const headers = new HttpHeaders();
    headers.append('Content-Type', 'application/json;charset=utf-8');

    const compiledUrlTemplate = urltemplate.parse(url);

    url = compiledUrlTemplate.expand({ resourceId: busClassName });

    return <Observable<LMFieldPickerResponse>> this.http.post(url, JSON.stringify(formRequest), { headers }).pipe(
      catchError(error => this._handleError(error)));
  }

  public computeIsValid(url: string, busClassName: string, field: LMPersonalizationColumn): Observable<LMLplValidationResponse> {
    const formRequest: any = {
      requestType: 'validateCompute',
      param1: busClassName,
      param2: field.name,
      param3: field.compute,
    };

    const headers = new HttpHeaders();
    headers.append('Content-Type', 'application/json;charset=utf-8');

    const compiledUrlTemplate = urltemplate.parse(url);

    url = compiledUrlTemplate.expand({ resourceId: busClassName });

    return <Observable<LMLplValidationResponse>> this.http.post(url, JSON.stringify(formRequest), { headers }).pipe(
      catchError(error => this._handleError(error)));
  }

  public conditionIsValid(value: string, url: string, busClassName: string, computeType?: ValidationType): Observable<LMLplValidationResponse> {
    const formRequest: any = {
      requestType: 'validateCondition',
      param1: busClassName,
      param2: value,
    };
    if (computeType === 'metric-total')
      formRequest.computeType = computeType;

    const headers = new HttpHeaders();
    headers.append('Content-Type', 'application/json;charset=utf-8');

    return <Observable<LMLplValidationResponse>>this.http.post(url, JSON.stringify(formRequest), { headers }).pipe(
      catchError(error => this._handleError(error)));
  }

  public runPersonalizeAction(url: string, requestParams?: any): Observable<LMPersonalizeActionResponse> {
    return <Observable<LMPersonalizeActionResponse>> this.http.post(url, JSON.stringify(requestParams)).pipe(catchError(error => {
      this.lmLoggerService.error(error);
      return throwError(error || 'Server error');
    }));
  }

  public createReport(
    url: string, 
    dataView: LMReportPersonalizationDataView, 
    displayFields: any[],
    userFolder?: string, 
    cubeInfo?: LMCubeReportInfo, 
    viewReport?: boolean, 
    calledOutActions?: LMReportPersonalizationCalledOutAction[], 
    restrictedActions?: LMReportPersonalizationRestrictedAction[]
  ): Observable<LMCreateReportPersonalizeResponse> {
    return this.createReportAndPersonalize(url, dataView, displayFields, userFolder, cubeInfo, viewReport, calledOutActions, restrictedActions);
  }

  private createReportAndPersonalize(
    url: string, 
    reportDataView: LMReportPersonalizationDataView, 
    displayFields: LMPersonalizationColumn[],
    userFolder?: string, 
    cubeInfo?: LMCubeReportInfo, 
    viewReport?: boolean, 
    calledOutActions?: LMReportPersonalizationCalledOutAction[], 
    restrictedActions?: LMReportPersonalizationRestrictedAction[]
  ): Observable<LMCreateReportPersonalizeResponse> {
    const formRequest: LMReportFormRequest = {
      requestType: 'createReportAndPersonalize',
      displayFields,
      cubeInfo,
      reportDataView,
      viewReport,
      calledOutActions,
      restrictedActions
    };

    url = this.lmUrlUtilService.updateUrlParams(url, { userfolder: userFolder });
    return this.makeFormRequest(url, formRequest);
  }

  public personalize(
    url: string, 
    reqDataView: LMReportPersonalizationDataView, 
    displayFields: LMPersonalizationColumn[], 
    userFolder: string, 
    cubeInfo?: LMCubeReportInfo, 
    calledOutActions?: LMReportPersonalizationCalledOutAction[], 
    restrictedActions?: LMReportPersonalizationRestrictedAction[]
  ): Observable<LMCreateReportPersonalizeResponse> {
    const formRequest: LMPersonalizeFormRequest = {
      requestType: 'saveListPersonalization',
      displayFields,
      reqDataView,
      cubeInfo,
      calledOutActions,
      restrictedActions
    };

    url = this.lmUrlUtilService.updateUrlParams(url, { userfolder: userFolder });
    return this.makeFormRequest(url, formRequest);
  }

  public getUserListLPL<T>(url: string, userfolder?: string) {
    const formRequest = {
      requestType: 'getUserListLPL',
      userfolder
    };

    const headers = new HttpHeaders();
    headers.append('Content-Type', 'application/json;charset=utf-8');

    return this.http
    .post<T>(url, formRequest).pipe(
      catchError(error => {
        this.lmLoggerService.error(error);
        return throwError(error || 'Server error');
      }));
  }

  public saveUserListLPL<T>(url: string, lpl: string, userfolder?: string) {
    const formRequest = {
      requestType: 'saveUserListLPL',
      lpl,
      userfolder
    };

    const headers = new HttpHeaders();
    headers.append('Content-Type', 'application/json;charset=utf-8');

    return this.http
    .post<T>(url, formRequest).pipe(
      catchError(error => {
        this.lmLoggerService.error(error);
        return throwError(error || 'Server error');
      }));
  }

  public getCubeInfo(url: string, businessClass: string, listName: string, userfolderId?: string) {
    const formRequest = {
      requestType: 'cubeInfo',
      businessClass,
      listName,
      userfolderId,
    };

    const headers = new HttpHeaders();
    headers.append('Content-Type', 'application/json;charset=utf-8');

    return this.http
    .post(url, formRequest).pipe(
      catchError(error => {
        this.lmLoggerService.error(error);
        return throwError(error || 'Server error');
      }));
  }

  private makeFormRequest(url: string, formRequest: LMReportFormRequest | LMPersonalizeFormRequest): Observable<LMCreateReportPersonalizeResponse> {
    const headers = new HttpHeaders();
    headers.append('Content-Type', 'application/json;charset=utf-8');

    return <Observable<LMCreateReportPersonalizeResponse>>this.http.post(url, JSON.stringify(formRequest), { headers }).pipe(
      catchError(error => this._handleError(error)));
  }

  private _handleError(error: Response) {
    // in a real world app, we may send the error to some remote logging infrastructure
    // instead of just logging it to the console
    this.lmLoggerService.log(error);
    return throwError(error || 'Server error');
  }

  public fetchUserListViews(url: string, lk?: string): Observable<LMUserListViewResponse> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json;charset=utf-8' });
    const body = { lk, requestType: 'fetchUserListViews' };

    return this.http.post<LMUserListViewResponse>(url, JSON.stringify(body), { headers }).pipe(
      catchError(error => this._handleError(error)));
  }

  public saveUserListView(url: string, dataView: LMDataView): Observable<any> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json;charset=utf-8' });
    const body = { dataView, requestType: 'saveUserListView' };

    return this.http.post(url, JSON.stringify(body), { headers }).pipe(
      catchError(error => this._handleError(error)));
  }

  public getDefaultUserListView(url: string): Observable<LMUserListViewDefaultResponse> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json;charset=utf-8' });
    const body = { requestType: 'defaultUserListView' };

    return this.http.post<LMUserListViewDefaultResponse>(url, JSON.stringify(body), { headers }).pipe(
      catchError(error => this._handleError(error)));
  }
}

export interface PersonalizeParams {
  sourceResourceUrl: string;
  listStateInfo: LMListStateInfo;
}
